import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { 
  ChevronRight, 
  MapPin, 
  Clock, 
  Banknote,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Star,
  TrendingDown,
  TrendingUp,
  Minus,
  FileText,
  Users,
  ShieldCheck,
  Flag,
  MessageSquare,
  Sparkles,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import WorkerAvatar from '../../components/shared/WorkerAvatar';
import { formatApiError } from '../../lib/apiError';
import { Textarea } from '../../components/ui/textarea';

const getDiscColor = (discrepancy) => {
  if (discrepancy === 0) return { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' };
  const abs = Math.abs(discrepancy);
  if (abs <= 5) return { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' };
  return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' };
};

const logTypeLabels = { start: 'Shift Start', mid_check: 'Mid-Shift Check', end: 'Shift End' };

export default function LogsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { shiftId } = useParams();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('all');

  useEffect(() => {
    if (shiftId) return;
    fetchShifts();
  }, [shiftId]);

  const fetchShifts = async () => {
    try {
      const response = await api.get('/shifts');
      setShifts(response.data);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: 'bg-blue-100 text-blue-800',
      assigned: 'bg-amber-100 text-amber-800',
      in_progress: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-slate-100 text-slate-800',
      disputed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600',
      no_show: 'bg-red-100 text-red-800',
    };
    return <Badge className={`${styles[status] || 'bg-slate-100'} font-medium`}>{status?.replace('_', ' ')}</Badge>;
  };

  if (shiftId) return <ShiftDetailView shiftId={shiftId} />;

  const filteredShifts = selectedTab === 'all' ? shifts : shifts.filter(s => s.status === selectedTab);

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" /><Skeleton className="h-12 w-full" />
        <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="logs-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']">Shift Logs</h1>
        <p className="text-slate-500 mt-1">View detailed shift history, till audits, and accountability</p>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto">
          <TabsTrigger value="all" className="rounded-lg min-h-[44px] min-w-[44px] px-4">All</TabsTrigger>
          <TabsTrigger value="in_progress" className="rounded-lg min-h-[44px] px-4">Active</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-lg min-h-[44px] px-4">Completed</TabsTrigger>
          <TabsTrigger value="disputed" className="rounded-lg min-h-[44px] px-4">Disputed</TabsTrigger>
        </TabsList>
      </Tabs>

      {filteredShifts.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Shifts Found</h3>
            <p className="text-slate-500">No shifts match the selected filter</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredShifts.map(shift => (
            <Card
              key={shift.id}
              className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/manager/logs/${shift.id}`)}
              data-testid={`shift-log-${shift.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{shift.branchName}</h3>
                      {getStatusBadge(shift.status)}
                      {shift.penaltyCount > 0 && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">&pound;{shift.penaltyTotal?.toFixed(2)} penalty</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span>{shift.date}</span>
                      <div className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /><span>{shift.scheduledStartTime} - {shift.scheduledEndTime}</span></div>
                      <div className="flex items-center gap-1"><Banknote className="h-3.5 w-3.5" /><span>&pound;{shift.pay?.toFixed(2)}</span></div>
                    </div>
                    {shift.assignedWorkerName && (
                      <div className="flex items-center gap-2 mt-2">
                        <WorkerAvatar
                          profilePhoto={shift.assignedWorkerPhoto}
                          name={shift.assignedWorkerName}
                          size={28}
                          testId={`logs-worker-avatar-${shift.id}`}
                        />
                        <p className="text-sm text-slate-600"><Users className="h-3.5 w-3.5 inline mr-1" />Worker: <span className="font-medium">{shift.assignedWorkerName}</span></p>
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ======= Shift Detail View =======
function ShiftDetailView({ shiftId }) {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [ratingLoading, setRatingLoading] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  // Discrepancy resolve modal state
  const [resolveModal, setResolveModal] = useState({ open: false, checkId: null });
  const [resolveReason, setResolveReason] = useState('');
  const [resolveNote, setResolveNote] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const location = useLocation();

  // Manager internal notes (visible to managers/admins only — never workers).
  const [managerNotes, setManagerNotes] = useState('');
  const [managerNotesDirty, setManagerNotesDirty] = useState(false);
  const [managerNotesSaving, setManagerNotesSaving] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [markReviewedBusy, setMarkReviewedBusy] = useState(false);

  // Contextual back button: preserve where the manager came from so
  // navigating back matches their expectation. Priority: explicit state
  // > referring URL prefix > pathname prefix > sensible default.
  const backTarget = (() => {
    const stateFrom = location.state && location.state.from;
    if (stateFrom === 'alerts') return { label: 'Back to Alerts', to: '/manager/alerts' };
    if (stateFrom === 'shift-records') return { label: 'Back to Shift Records', to: '/manager/shift-records' };
    if (location.pathname.startsWith('/manager/shift-records/')) {
      return { label: 'Back to Shift Records', to: '/manager/shift-records' };
    }
    if (typeof document !== 'undefined') {
      const ref = document.referrer || '';
      if (ref.includes('/manager/alerts')) return { label: 'Back to Alerts', to: '/manager/alerts' };
      if (ref.includes('/manager/shift-records')) return { label: 'Back to Shift Records', to: '/manager/shift-records' };
    }
    return { label: 'Back to Shift Logs', to: '/manager/logs' };
  })();

  useEffect(() => { fetchShiftLog(); }, [shiftId]);

  // Auto-scroll to a specific discrepancy card when navigating in with a
  // `#disc-<checkId>` URL hash (e.g. from the Alerts page "View shift log"
  // link). We wait for the shift log to load AND the DOM to paint before
  // scrolling so the target is real.
  useEffect(() => {
    if (loading || !data) return;
    const hash = location.hash || '';
    if (!hash.startsWith('#disc-')) return;
    // Two rAFs guarantees the discrepancy card has committed layout.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-rose-400');
        setTimeout(() => el.classList.remove('ring-2', 'ring-rose-400'), 2200);
      }
    }));
  }, [loading, data, location.hash]);

  const fetchShiftLog = async () => {
    try {
      const response = await api.get(`/shifts/${shiftId}/full-log`);
      setData(response.data);
      // Hydrate manager notes from the shift record (empty string when absent).
      const notesFromShift = response.data?.shift?.managerNotes || '';
      setManagerNotes(notesFromShift);
      setManagerNotesDirty(false);
    } catch (error) {
      toast.error('Failed to load shift details');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveManagerNotes = async () => {
    setManagerNotesSaving(true);
    try {
      await api.put(`/manager/shift-records/${shiftId}/notes`, { notes: managerNotes });
      toast.success('Notes saved');
      setManagerNotesDirty(false);
      await fetchShiftLog();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save notes'));
    } finally {
      setManagerNotesSaving(false);
    }
  };

  const handleMarkShiftReviewed = async () => {
    setMarkReviewedBusy(true);
    try {
      await api.post(`/manager/shift-records/${shiftId}/mark-reviewed`);
      toast.success('Shift record marked as reviewed');
      await fetchShiftLog();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to mark as reviewed'));
    } finally {
      setMarkReviewedBusy(false);
    }
  };

  const handleSuggestNote = async () => {
    setSuggestBusy(true);
    try {
      const r = await api.post(`/manager/shift-records/${shiftId}/suggest-note`);
      const suggestion = r.data?.suggestion || '';
      if (!suggestion) {
        toast.error('AI returned no suggestion — please try again');
        return;
      }
      setManagerNotes(suggestion);
      setManagerNotesDirty(true);
      toast.success('Suggested — edit and Save when you\u2019re happy');
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to fetch suggestion'));
    } finally {
      setSuggestBusy(false);
    }
  };

  const handleTillReview = async (status) => {
    if (!data?.tillLog) return;
    setReviewLoading(true);
    try {
      await api.patch(`/till-logs/${data.tillLog.id}/review`, { status });
      toast.success(`Till log ${status}`);
      fetchShiftLog();
    } catch { toast.error('Failed to update review'); }
    finally { setReviewLoading(false); }
  };

  const handleSubmitRating = async () => {
    if (rating === 0) { toast.error('Please select a rating'); return; }
    setRatingLoading(true);
    try {
      await api.post('/ratings', { shiftId, workerId: data.worker.id, ratingValue: rating, comment });
      toast.success('Rating submitted!');
      fetchShiftLog();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit rating'); }
    finally { setRatingLoading(false); }
  };

  const handleMarkDiscrepancyReviewed = async (checkId) => {
    setActionBusy(true);
    try {
      await api.patch(`/discrepancy-checks/${checkId}/review`);
      toast.success('Marked as reviewed');
      await fetchShiftLog();
    } catch (err) { toast.error(formatApiError(err, 'Failed to update')); }
    finally { setActionBusy(false); }
  };

  const openResolveModal = (checkId) => {
    setResolveReason('');
    setResolveNote('');
    setResolveModal({ open: true, checkId });
  };

  const handleResolveDiscrepancy = async () => {
    if (!resolveReason) { toast.error('Please choose a reason'); return; }
    setActionBusy(true);
    try {
      const res = await api.post(`/discrepancy-checks/${resolveModal.checkId}/resolve`, {
        reason: resolveReason,
        note: resolveNote,
      });
      const refundText = res.data?.scoreRefunded
        ? " Worker's reliability score refunded."
        : ' (Score kept for auditing.)';
      toast.success('Discrepancy resolved.' + refundText);
      setResolveModal({ open: false, checkId: null });
      await fetchShiftLog();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to resolve'));
    } finally { setActionBusy(false); }
  };

  const handleEscalateDiscrepancy = async (checkId) => {
    const note = window.prompt('Escalation note (optional):') || '';
    setActionBusy(true);
    try {
      await api.post(`/discrepancy-checks/${checkId}/escalate`, { note });
      toast.success('Discrepancy escalated — will stay on your active alerts.');
      await fetchShiftLog();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to escalate'));
    } finally { setActionBusy(false); }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div className="p-6 space-y-6"><Skeleton className="h-10 w-48" /><Skeleton className="h-32" /><Skeleton className="h-48" /></div>
  );

  if (!data) return <div className="p-6"><p className="text-slate-500">Shift not found</p></div>;

  const { shift, worker, timeLog, tillLog, discrepancyChecks, rating: existingRating, tillAuditLogs, tillAuditSummary } = data;

  return (
    <div className="p-6 space-y-6" data-testid="shift-detail-view">
      <Button
        variant="ghost"
        className="text-slate-600 hover:text-slate-900 -ml-2"
        onClick={() => navigate(backTarget.to)}
        data-testid="shift-detail-back-btn"
      >
        <ArrowLeft className="h-4 w-4 mr-2" /> {backTarget.label}
      </Button>

      {/* Review status header — surfaces the shift-record lifecycle
          (Not Reviewed / Under Review / Reviewed / Resolved) and a
          single-click "Mark as Reviewed" action. */}
      {(() => {
        const openDiscForStatus = (discrepancyChecks || []).filter(c => c.discrepancyAmount !== 0 && (c.resolutionStatus || c.status) !== 'resolved');
        const allDisc = (discrepancyChecks || []).filter(c => c.discrepancyAmount !== 0);
        let reviewStatus;
        if (shift.shiftReviewed) reviewStatus = 'reviewed';
        else if (allDisc.length > 0 && openDiscForStatus.length === 0) reviewStatus = 'resolved';
        else if (allDisc.some(c => ['reviewed', 'under_review'].includes(c.resolutionStatus))) reviewStatus = 'under_review';
        else reviewStatus = 'not_reviewed';

        const badgeMap = {
          not_reviewed: { label: 'Not Reviewed', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
          under_review: { label: 'Under Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
          reviewed:     { label: 'Reviewed',     cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
          resolved:     { label: 'Resolved',     cls: 'bg-blue-100 text-blue-800 border-blue-200' },
        };
        const b = badgeMap[reviewStatus];
        return (
          <div
            className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 flex-wrap"
            data-testid="shift-review-status-header"
          >
            <div className="flex items-center gap-3 min-w-0">
              <Badge className={`${b.cls} border font-semibold text-[11px] uppercase tracking-wider px-2.5 py-1`} data-testid={`review-status-badge-${reviewStatus}`}>
                {b.label}
              </Badge>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-slate-900">Shift record status</p>
                <p className="text-[11.5px] text-slate-500">
                  {reviewStatus === 'reviewed' && `Reviewed by ${shift.shiftReviewedByName || 'you'}`}
                  {reviewStatus === 'resolved' && 'All discrepancies resolved'}
                  {reviewStatus === 'under_review' && 'One or more discrepancies are being investigated'}
                  {reviewStatus === 'not_reviewed' && 'Manager review pending'}
                </p>
              </div>
            </div>
            {!shift.shiftReviewed && (
              <Button
                size="sm"
                className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white h-10"
                disabled={markReviewedBusy}
                onClick={handleMarkShiftReviewed}
                data-testid="mark-shift-reviewed-btn"
              >
                {markReviewedBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Mark as Reviewed
              </Button>
            )}
          </div>
        );
      })()}

      {/* Open-discrepancy action banner — pinned at the top so the manager
          always has a clear path to resolve, even before scrolling. */}
      {(() => {
        const openDiscrepancies = (discrepancyChecks || []).filter((c) => {
          const rs = c.resolutionStatus || (c.status === 'discrepancy_reported' ? 'open' : c.status);
          return c.discrepancyAmount !== 0 && (rs === 'open' || rs === 'reviewed' || rs === 'escalated' || rs === 'discrepancy_reported');
        });
        if (openDiscrepancies.length === 0) return null;
        const first = openDiscrepancies[0];
        return (
          <div
            className="rounded-xl border border-rose-300 bg-rose-50 p-4 flex items-start gap-3"
            data-testid="discrepancy-action-banner"
          >
            <div className="h-9 w-9 rounded-lg bg-rose-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-rose-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13.5px] font-semibold text-rose-900">
                {openDiscrepancies.length === 1
                  ? '1 discrepancy needs your action'
                  : `${openDiscrepancies.length} discrepancies need your action`}
              </p>
              <p className="text-[11.5px] text-rose-700 mt-0.5">
                Choose Mark reviewed, Resolve (with a reason), or Escalate for each one below.
              </p>
            </div>
            <Button
              size="sm"
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white h-9 shrink-0"
              onClick={() => {
                const el = document.getElementById(`disc-${first.id}`);
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  el.classList.add('ring-2', 'ring-rose-400');
                  setTimeout(() => el.classList.remove('ring-2', 'ring-rose-400'), 2200);
                }
              }}
              data-testid="discrepancy-banner-jump-btn"
            >
              Review now
            </Button>
          </div>
        );
      })()}

      {/* Shift Info */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-['Public_Sans']">{shift.branchName}</h2>
              <div className="flex items-center gap-1 text-slate-500 mt-1"><MapPin className="h-4 w-4" /><span>{shift.location || '—'}</span></div>
            </div>
            <Badge className={`${shift.status === 'completed' ? 'bg-slate-100 text-slate-800' : shift.status === 'in_progress' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800'} font-medium`}>
              {shift.status?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-slate-500">Date</p><p className="font-medium text-slate-900">{shift.date}</p></div>
            <div><p className="text-slate-500">Scheduled</p><p className="font-medium text-slate-900">{shift.scheduledStartTime} - {shift.scheduledEndTime}</p></div>
            <div><p className="text-slate-500">Break</p><p className="font-medium text-slate-900">{shift.breakDurationMinutes} min</p></div>
            <div><p className="text-slate-500">Pay</p><p className="font-medium text-slate-900">{typeof shift.pay === 'number' ? `\u00a3${shift.pay.toFixed(2)}` : '—'}</p></div>
          </div>
          {worker && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-3">
              <WorkerAvatar
                profilePhoto={worker.profilePhoto}
                name={worker.fullName}
                size={40}
                testId="logs-detail-worker-avatar"
              />
              <div><p className="font-medium text-slate-900">{worker.fullName}</p><p className="text-sm text-slate-500">{worker.email}</p></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manager Notes — internal, only visible to managers/admins.
          Backed by PUT /api/manager/shift-records/{shift_id}/notes; worker
          responses have this field stripped server-side. */}
      <Card className="border-0 shadow-sm" data-testid="manager-notes-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg font-semibold font-['Public_Sans']">
              Manager Notes
              <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Internal — not visible to workers</span>
            </CardTitle>
            {shift.managerNotesUpdatedAt && (
              <span className="text-[11.5px] text-slate-500">
                Last edited {new Date(shift.managerNotesUpdatedAt).toLocaleString('en-GB')}
                {shift.managerNotesUpdatedByName ? ` by ${shift.managerNotesUpdatedByName}` : ''}
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Textarea
            value={managerNotes}
            onChange={(e) => { setManagerNotes(e.target.value); setManagerNotesDirty(true); }}
            placeholder="Add an internal note (e.g., Worker performed well, Required additional support, Excellent customer service, Training recommended)…"
            className="min-h-[100px] text-[13.5px] rounded-lg border-slate-200"
            maxLength={4000}
            data-testid="manager-notes-textarea"
          />
          <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
            <span className="text-[11px] text-slate-400">{managerNotes.length} / 4000</span>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                size="sm"
                variant="outline"
                className="rounded-full h-9 border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={handleSuggestNote}
                disabled={suggestBusy || managerNotesSaving}
                data-testid="manager-notes-suggest-btn"
                title="Draft a note with AI — you always review before saving"
              >
                {suggestBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1 text-violet-600" />}
                {suggestBusy ? 'Drafting…' : 'Suggest with AI'}
              </Button>
              <Button
                size="sm"
                className="rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white h-9"
                onClick={handleSaveManagerNotes}
                disabled={!managerNotesDirty || managerNotesSaving}
                data-testid="manager-notes-save-btn"
              >
                {managerNotesSaving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                {managerNotesSaving ? 'Saving…' : 'Save note'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Log */}
      {timeLog && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold font-['Public_Sans']">Time Log</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Start Time</p>
                <p className="font-medium text-slate-900">{formatTime(timeLog.actualStartTime)}</p>
                {timeLog.lateFlag && <Badge className="bg-amber-100 text-amber-800 mt-1">Started late</Badge>}
              </div>
              <div>
                <p className="text-sm text-slate-500">Break</p>
                <p className="font-medium text-slate-900">{timeLog.breakStartTime ? `${formatTime(timeLog.breakStartTime)} - ${formatTime(timeLog.breakEndTime)}` : '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">End Time</p>
                <p className="font-medium text-slate-900">{formatTime(timeLog.actualEndTime)}</p>
                {timeLog.leftEarlyFlag && <Badge className="bg-amber-100 text-amber-800 mt-1">Left early</Badge>}
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Worked</p>
                <p className="font-medium text-slate-900">{timeLog.totalWorkedMinutes} min</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ====== TILL AUDIT TIMELINE ====== */}
      {tillAuditLogs && tillAuditLogs.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="till-audit-timeline">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold font-['Public_Sans']">Till Audit Timeline</CardTitle>
              {tillAuditSummary && (
                <Badge className={`${tillAuditSummary.netDiscrepancy === 0 ? 'bg-emerald-100 text-emerald-800' : Math.abs(tillAuditSummary.netDiscrepancy) <= 5 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'} text-xs`}>
                  Net: {tillAuditSummary.netDiscrepancy === 0 ? 'Balanced' : `\u00a3${Math.abs(tillAuditSummary.netDiscrepancy).toFixed(2)} ${tillAuditSummary.netDiscrepancy < 0 ? 'short' : 'over'}`}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Summary Cards */}
            {tillAuditSummary && (
              <div className="grid grid-cols-4 gap-3 mb-6">
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">Start</p>
                  <p className="text-lg font-bold text-slate-900">&pound;{tillAuditSummary.startAmount?.toFixed(2) || '-'}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">End</p>
                  <p className="text-lg font-bold text-slate-900">{tillAuditSummary.endAmount != null ? `\u00a3${tillAuditSummary.endAmount.toFixed(2)}` : 'Pending'}</p>
                </div>
                <div className={`p-3 rounded-xl text-center ${getDiscColor(tillAuditSummary.netDiscrepancy).bg}`}>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">Net</p>
                  <p className={`text-lg font-bold ${getDiscColor(tillAuditSummary.netDiscrepancy).text}`}>
                    {tillAuditSummary.netDiscrepancy === 0 ? 'Balanced' : `\u00a3${Math.abs(tillAuditSummary.netDiscrepancy).toFixed(2)}`}
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-xl text-center">
                  <p className="text-[11px] text-slate-500 uppercase tracking-wide">Checks</p>
                  <p className="text-lg font-bold text-slate-900">{tillAuditSummary.checkCount}</p>
                  {tillAuditSummary.discrepancyCount > 0 && (
                    <p className="text-[10px] text-red-600">{tillAuditSummary.discrepancyCount} with issues</p>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="space-y-0 relative">
              <div className="absolute left-[11px] top-3 bottom-3 w-0.5 bg-slate-200" />
              
              {tillAuditLogs.map((log) => {
                const colors = getDiscColor(log.discrepancy);
                const isFirstDisc = tillAuditSummary?.firstDiscrepancyId === log.id;
                
                return (
                  <div
                    key={log.id}
                    className={`relative pl-8 py-3 ${isFirstDisc ? 'ring-2 ring-red-300 rounded-lg ml-[-8px] pl-[40px] bg-red-50/50' : ''}`}
                    data-testid={`timeline-log-${log.id}`}
                  >
                    <div className={`absolute left-0 top-4 h-6 w-6 rounded-full flex items-center justify-center z-10 ${colors.bg} border-2 ${colors.border}`}>
                      {log.logType === 'start' ? <Banknote className={`h-3 w-3 ${colors.text}`} /> :
                       log.logType === 'end' ? <CheckCircle2 className={`h-3 w-3 ${colors.text}`} /> :
                       log.discrepancy === 0 ? <Minus className={`h-3 w-3 ${colors.text}`} /> :
                       log.discrepancy < 0 ? <TrendingDown className={`h-3 w-3 ${colors.text}`} /> :
                       <TrendingUp className={`h-3 w-3 ${colors.text}`} />}
                    </div>

                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${colors.bg} ${colors.text} text-xs`}>{logTypeLabels[log.logType]}</Badge>
                          <span className="text-xs text-slate-400">{formatTime(log.timestamp)}</span>
                          {isFirstDisc && <Badge className="bg-red-100 text-red-700 text-xs font-bold">First Discrepancy</Badge>}
                        </div>
                        {log.note && <p className="text-sm text-slate-600 mt-1 italic">&ldquo;{log.note}&rdquo;</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-slate-900">&pound;{log.actualAmount.toFixed(2)}</p>
                        {log.logType !== 'start' && (
                          <>
                            <p className={`text-xs font-medium ${colors.text}`}>
                              {log.discrepancy === 0 ? 'Balanced' : `${log.discrepancy > 0 ? '+' : ''}\u00a3${log.discrepancy.toFixed(2)}`}
                            </p>
                            <p className="text-[10px] text-slate-400">exp: &pound;{log.expectedAmount.toFixed(2)}</p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy Discrepancy Checks (for older shifts) */}
      {(!tillAuditLogs || tillAuditLogs.length === 0) && discrepancyChecks && discrepancyChecks.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="discrepancy-checks-card">
          <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold font-['Public_Sans']">Till Check History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4">
              {discrepancyChecks.map((check) => {
                const rs = check.resolutionStatus || (check.status === 'reviewed' ? 'reviewed' : (check.status === 'discrepancy_reported' ? 'open' : null));
                const isOpen = rs === 'open' || (rs == null && check.status === 'discrepancy_reported');
                const isResolved = rs === 'resolved';
                const isEscalated = rs === 'escalated';
                return (
                  <div
                    key={check.id}
                    id={`disc-${check.id}`}
                    className={`relative pl-8 pb-4 border-b border-slate-100 last:border-0 last:pb-0 transition-shadow rounded-md ${
                      isOpen ? 'bg-rose-50/30 -mx-2 px-2' : ''
                    }`}
                    data-testid={`discrepancy-check-${check.id}`}
                    data-resolution-status={rs || 'open'}
                  >
                    <div className={`absolute left-0 top-0 h-6 w-6 rounded-full flex items-center justify-center ${check.discrepancyType === 'balanced' ? 'bg-emerald-100' : check.discrepancyType === 'shortage' ? 'bg-red-100' : 'bg-amber-100'}`}>
                      {check.discrepancyType === 'balanced' ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className={`h-4 w-4 ${check.discrepancyType === 'shortage' ? 'text-red-600' : 'text-amber-600'}`} />}
                    </div>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm text-slate-500">{formatTime(check.checkTimestamp)}</p>
                          {isOpen && <Badge className="bg-rose-100 text-rose-700 border border-rose-200 text-[10px] font-bold uppercase tracking-wider">Action needed</Badge>}
                          {rs === 'reviewed' && <Badge className="bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider">Reviewed</Badge>}
                          {isResolved && <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-wider" data-testid={`disc-status-resolved-${check.id}`}>Resolved</Badge>}
                          {isEscalated && <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-bold uppercase tracking-wider" data-testid={`disc-status-escalated-${check.id}`}>Escalated</Badge>}
                        </div>
                        <p className="font-medium text-slate-900 capitalize mt-1">{check.discrepancyType}</p>
                        {check.discrepancyAmount !== 0 && <p className={`text-lg font-bold ${check.discrepancyAmount < 0 ? 'text-red-600' : 'text-amber-600'}`}>&pound;{Math.abs(check.discrepancyAmount).toFixed(2)} {check.discrepancyAmount < 0 ? 'short' : 'over'}</p>}
                        {check.explanation && <p className="text-sm text-slate-600 mt-1">&ldquo;{check.explanation}&rdquo;</p>}
                        {isResolved && (
                          <p className="text-[12px] text-emerald-800 mt-1.5 leading-relaxed" data-testid={`disc-resolution-note-${check.id}`}>
                            <ShieldCheck className="h-3.5 w-3.5 inline mr-1" />
                            Discrepancy reported — resolved by {check.resolvedByName || 'manager'} as{' '}
                            <strong>{(check.resolutionReason || '').replace(/_/g, ' ')}</strong>.
                            {check.resolutionNote && <span className="italic block mt-0.5">&ldquo;{check.resolutionNote}&rdquo;</span>}
                          </p>
                        )}
                        {isEscalated && check.escalationNote && (
                          <p className="text-[12px] text-amber-800 mt-1.5">
                            <Flag className="h-3.5 w-3.5 inline mr-1" /> {check.escalationNote}
                          </p>
                        )}
                      </div>
                      {check.discrepancyAmount !== 0 && !isResolved && (
                        <div className="flex gap-1.5 flex-wrap">
                          {isOpen && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-8 text-[11.5px] px-2.5"
                              onClick={() => handleMarkDiscrepancyReviewed(check.id)}
                              disabled={actionBusy}
                              data-testid={`disc-mark-reviewed-${check.id}`}
                            >
                              Mark reviewed
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="rounded-full h-8 text-[11.5px] px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => openResolveModal(check.id)}
                            disabled={actionBusy}
                            data-testid={`disc-resolve-btn-${check.id}`}
                          >
                            <ShieldCheck className="h-3 w-3 mr-1" /> Resolve
                          </Button>
                          {!isEscalated && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="rounded-full h-8 text-[11.5px] px-2.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                              onClick={() => handleEscalateDiscrepancy(check.id)}
                              disabled={actionBusy}
                              data-testid={`disc-escalate-btn-${check.id}`}
                            >
                              <Flag className="h-3 w-3 mr-1" /> Escalate
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy Till Log (for older shifts) */}
      {(!tillAuditLogs || tillAuditLogs.length === 0) && tillLog && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold font-['Public_Sans']">Final Till Log</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div><p className="text-sm text-slate-500">Start Amount</p><p className="text-xl font-bold text-slate-900">&pound;{tillLog.startTillAmount.toFixed(2)}</p></div>
              <div><p className="text-sm text-slate-500">End Amount</p><p className="text-xl font-bold text-slate-900">&pound;{tillLog.endTillAmount.toFixed(2)}</p></div>
              <div><p className="text-sm text-slate-500">Difference</p><p className={`text-xl font-bold ${tillLog.differenceAmount === 0 ? 'text-emerald-600' : tillLog.differenceAmount < 0 ? 'text-red-600' : 'text-amber-600'}`}>{tillLog.differenceAmount === 0 ? 'Balanced' : `\u00a3${Math.abs(tillLog.differenceAmount).toFixed(2)} ${tillLog.differenceAmount < 0 ? 'short' : 'over'}`}</p></div>
            </div>
            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
              <Badge className={`${tillLog.managerReviewStatus === 'approved' ? 'bg-emerald-100 text-emerald-800' : tillLog.managerReviewStatus === 'disputed' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{tillLog.managerReviewStatus}</Badge>
              {tillLog.managerReviewStatus === 'pending' && (
                <div className="flex gap-2 ml-auto">
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => handleTillReview('approved')} disabled={reviewLoading}>Approve</Button>
                  <Button size="sm" variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50" onClick={() => handleTillReview('disputed')} disabled={reviewLoading}>Dispute</Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rating Section */}
      {worker && shift.status === 'completed' && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-lg font-semibold font-['Public_Sans']">Staff Rating</CardTitle></CardHeader>
          <CardContent>
            {existingRating ? (
              <div className="flex items-center gap-4">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => <Star key={s} className={`h-6 w-6 ${s <= existingRating.ratingValue ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />)}
                </div>
                {existingRating.comment && <p className="text-slate-600">&ldquo;{existingRating.comment}&rdquo;</p>}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => setRating(s)} className="p-1 transition-transform hover:scale-110">
                      <Star className={`h-8 w-8 ${s <= rating ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
                <input type="text" placeholder="Add a comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50" />
                <Button className="rounded-full bg-[#DA291C] hover:bg-[#b91f13]" onClick={handleSubmitRating} disabled={ratingLoading || rating === 0} data-testid="submit-rating-btn">Submit Rating</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Resolve discrepancy modal */}
      <Dialog open={resolveModal.open} onOpenChange={(open) => setResolveModal({ open, checkId: open ? resolveModal.checkId : null })}>
        <DialogContent className="sm:max-w-md" data-testid="resolve-discrepancy-modal">
          <DialogHeader>
            <DialogTitle>Resolve discrepancy</DialogTitle>
            <DialogDescription>
              Pick a reason. If it&apos;s a no-fault reason we&apos;ll refund the worker&apos;s reliability-score penalty automatically. The record is still kept in the worker&apos;s history for full transparency.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider">Reason *</label>
              <div className="grid grid-cols-1 gap-1.5">
                {[
                  { value: 'counting_error', label: 'Counting error', hint: 'No score damage' },
                  { value: 'refund_correction', label: 'Refund correction', hint: 'No score damage' },
                  { value: 'training_issue', label: 'Training issue', hint: 'No score damage' },
                  { value: 'manager_approved', label: 'Manager approved', hint: 'No score damage' },
                  { value: 'other', label: 'Other', hint: 'Score kept — leaves an audit trail' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer ${
                      resolveReason === opt.value ? 'border-emerald-400 bg-emerald-50/40' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`resolve-reason-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name="resolve-reason"
                      value={opt.value}
                      checked={resolveReason === opt.value}
                      onChange={(e) => setResolveReason(e.target.value)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-[13px] font-semibold text-slate-900">{opt.label}</p>
                      <p className="text-[11px] text-slate-500">{opt.hint}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[12px] font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-1">
                <MessageSquare className="h-3 w-3" /> Manager note (optional)
              </label>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder='e.g. "Worker explained this was a refund correction. Manager reviewed and resolved."'
                maxLength={400}
                className="w-full min-h-[70px] rounded-lg border border-slate-200 text-[13px] px-3 py-2 resize-y"
                data-testid="resolve-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full h-11"
              onClick={() => setResolveModal({ open: false, checkId: null })}
              data-testid="resolve-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="rounded-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={actionBusy || !resolveReason}
              onClick={handleResolveDiscrepancy}
              data-testid="resolve-confirm-btn"
            >
              {actionBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><ShieldCheck className="h-4 w-4 mr-2" /> Confirm resolution</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
